import React, { useState, useEffect, memo } from "react";
import {
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Typography,
  Box,
  InputAdornment,
} from "@mui/material";
import { motion } from "framer-motion";
import configServ from "../../../../services/config";
import Calculation from "../../../../utility/Calculations";
import CardHeadMenu from "../../CardHeadMenu/CardHeadMenu";
import CheckAndReturn from "../../../../utility/CheckAndReturn";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import AddGoodsReceiveItems from "./AddGoodsReceiveItems";
import AlertDialog from "../../../AlertDialog/AlertDialog";
import { triggerLoader } from "../../../../redux/reducers/LoaderTrigger";
import Swal from "sweetalert2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

//FUNCTION
function GoodsReceiveCard({ editData, setEditData, isChanged, setIsChanged, setIsFormOpen }) {
  //#region code
  const dispatch = useDispatch();
  const { user_id, company_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables,
  );
  const [locations, setLocations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrderNumbers, setPurchaseOrderNumbers] = useState([]);
  const [items, setItems] = useState([]);
  const [autoGenerateGrn, setAutoGenerateGrn] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const itemListInitial = [
    {
      id: 1,
      itemFullname: "",
      item_id: "",
      hsn_code: "",
      order_qty: "",
      received_qty: "",
      rejected_qty: "",
      unit: "",
    },
  ];
  const [itemList, setItemList] = useState(itemListInitial);
  const itemListErrorInitial = [
    {
      itemFullname: "",
      order_qty: "",
      received_qty: "",
      rejected_qty: "",
    },
  ];
  const [itemListError, setItemListError] = useState(itemListErrorInitial);

  const goodsReceiveInitial = {
    status: true,
    receipt_date: Calculation.GetCurrentDate(),
    billing_date: Calculation.GetCurrentDate(),
    is_approved: "NO",
    grn_no: "",
    auto_generate_grn: true,
  };
  const [goodsReceive, setGoodsReceive] = useState(goodsReceiveInitial);
  const [goodsReceiveError, setGoodsReceiveError] = useState({});
  const [grnNoValid, setGrnNoValid] = useState(false);
  const [nextAutoNumbers, setNextAutoNumbers] = useState({
    purchase_order_no: "",
    purchase_bill_no: "",
    grn_no: "",
  });
  const [approvalRequired, setApprovalRequired] = useState(!(is_admin || is_super));

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const handleApprovalChange = (event) => {
    const isRequired = event.target.checked;
    setApprovalRequired(isRequired);
    setGoodsReceive(prev => ({
      ...prev,
      is_approved: isRequired ? "NO" : "YES"
    }));
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setGoodsReceive(editData);
      setApprovalRequired(editData.is_approved === "NO");
      const normalizedItems = (editData.items || []).map((item) => ({
        ...item,
        rejected_qty: item.rejected_qty || 0,
      }));
      const updateItemList = CheckAndReturn.UpdateItemList(normalizedItems);
      setItemList(updateItemList);
      setIsEdit(true);
      setIsCreate(true);
      setAutoGenerateGrn(false);
      // Load purchase orders for the supplier if editing
      if (editData.supplier_id) {
        getPurchaseOrderNumbers(editData.supplier_id);
      }
    } else {
      setIsEdit(false);
      setIsCreate(false);
      setAutoGenerateGrn(true);
      setGoodsReceive((prev) => ({
        ...prev,
        grn_no: nextAutoNumbers.grn_no || "",
      }));
    }
  }, [editData, nextAutoNumbers]);

  useEffect(() => {
    if (autoGenerateGrn && nextAutoNumbers.grn_no && !isEdit) {
      setGoodsReceive((prev) => ({
        ...prev,
        grn_no: nextAutoNumbers.grn_no,
      }));
    }
  }, [autoGenerateGrn, nextAutoNumbers, isEdit]);

  useEffect(() => {
    setGoodsReceive(prev => ({
      ...prev,
      is_approved: approvalRequired ? "NO" : "YES"
    }));
  }, [approvalRequired]);

  // Check GRN number validity
  const checkGrnNo = async () => {
    try {
      // For now, just check if it's not empty when manual entry
      // You can implement actual API check here if needed
      if (goodsReceive.grn_no && goodsReceive.grn_no.trim() !== "") {
        setGrnNoValid(true);
      } else {
        setGrnNoValid(false);
      }
    } catch (e) {
      console.error("Error checking GRN number:", e);
      setGrnNoValid(false);
    }
  };

  useEffect(() => {
    if (goodsReceive.grn_no && !autoGenerateGrn) {
      checkGrnNo();
    }
  }, [goodsReceive.grn_no, autoGenerateGrn]);

  // auto-select logged-in user when not admin
  useEffect(() => {
    if (!is_admin && !is_super && staffList.length > 0) {
      const loggedInStaff = staffList.find((s) => s.user_id === user_id);
      if (loggedInStaff) {
        handleInputChange({
          target: { name: "staff_id", value: loggedInStaff.id },
        });
      }
    }
  }, [staffList, is_admin, is_super]);

  useEffect(() => {
    if (company_id) {
      fetchLocationList();
      fetchStaffList();
      fetchSupplierList();
      fetchItemList();
      fetchNextGoodsReceiveNumbers();
    }
  }, [company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLocationList = async () => {
    if (!company_id) return;
    try {
      const dataToSend = {
        company_id: company_id,
      };
      const res = await configServ.getLocation(dataToSend);
      let locationData = Array.isArray(res) ? res : res.data || [];
      const result = locationData.filter((x) => x.status === 1 || x.status === true || x.status === "1");
      setLocations(result);
    } catch (e) {
      console.error("Error fetching locations:", e);
      setLocations([]);
    }
  };

  const fetchStaffList = async () => {
    if (!company_id) return;
    try {
      const dataToSend = {
        company_id: company_id,
        type: "staff",
      };
      const res = await configServ.getCustomerByType(dataToSend);
      
      // Handle both array response and object with data property
      let staffData = Array.isArray(res) ? res : res.data || [];
      
      // Filter active staff (status can be 1, true, or "1")
      const result = staffData.filter((x) => x.status === 1 || x.status === true || x.status === "1");
      setStaffList(result);
    } catch (e) {
      console.error("Error fetching staff list:", e);
      setStaffList([]);
    }
  };

  const fetchSupplierList = async () => {
    if (!company_id) return;
    try {
      const dataToSend = {
        company_id: company_id,
      };
      const res = await configServ.getSupplier(dataToSend);
      let supplierData = Array.isArray(res) ? res : res.data || [];
      const result = supplierData.filter((x) => x.status === 1 || x.status === true || x.status === "1");
      setSuppliers(result);
      console.log("Fetched suppliers:", result);
    } catch (e) {
      console.error("Error fetching suppliers:", e);
      setSuppliers([]);
    }
  };

  const getPurchaseOrderNumbers = async (supplierId = null) => {
    if (!company_id) return;
    try {
      const dataToSend = {
        company_id: company_id,
      };
      if (supplierId) {
        dataToSend.supplier_id = supplierId;
      }
      const res = await configServ.purchaseOrderNumbers(dataToSend);
      console.log("Purchase order numbers response:", res);
      let poData = Array.isArray(res) ? res : res.data || [];
      setPurchaseOrderNumbers(poData);
    } catch (e) {
      console.error("Error fetching purchase orders:", e);
      setPurchaseOrderNumbers([]);
    }
  };

  const handleOrderNoChange = async (purchaseOrderNo) => {
    try {
      setGoodsReceive((prev) => ({
        ...prev,
        purchase_order_no: purchaseOrderNo,
      }));

      if (purchaseOrderNo && !isEdit) {
        const dataToSend = {
          company_id: company_id,
          purchase_order_no: purchaseOrderNo,
        };
        const res = await configServ.purchaseOrderByOrderNo(dataToSend);
        console.log("Purchase order details response:", res);

        if (res.status === 200 && res.data) {
          const data = res.data;
          console.log("Purchase order data received:", data);
          console.log("Address fields in data:", {
            address: data.address,
            billtoaddress: data.billtoaddress,
            shiptoaddress: data.shiptoaddress,
            supplier_address: data.supplier?.address
          });

          // Populate form fields with purchase order data
          setGoodsReceive((prev) => ({
            ...prev,
            supplier_id: data.supplier_id || null,
            billing_date: data.delivery_date || "",
            reference: data.salesperson || "",
            address: data.shiptoaddress || data.billtoaddress || data.address || data.supplier?.address || "",
            purchase_order_no: data.purchase_order_no,
          }));

          // Populate items from purchase order
          if (data.item && Array.isArray(data.item)) {
            const itemsFromPO = data.item.map((item, index) => ({
              id: index + 1,
              item_id: item.itemid,
              hsn_code: item.hsn_code || "",
              order_qty: parseFloat(item.qty) || 0,
              received_qty: parseFloat(item.qty) || 0, // Default received qty to ordered qty
              rejected_qty: 0,
              unit: item.unit || "",
            }));
            setItemList(itemsFromPO);
          }
        }
      } else if (!purchaseOrderNo) {
        // Clear only item details when purchase order is cleared
        if (!isEdit) {
          setItemList(itemListInitial);
        }
      }
    } catch (e) {
      console.error("Error fetching purchase order details:", e);
    }
  };

  const fetchItemList = async () => {
    if (!company_id) return;
    try {
      const dataToSend = {
        company_id: company_id,
        search_by: null,
        search: null,
        per_page: null,
        page: null,
      };
      const res = await configServ.getItemList(dataToSend);
      console.log("Item list response:", res);
      let itemData = Array.isArray(res) ? res : res.data || [];
      if (itemData.length > 0) {
        const result = itemData.filter((x) => x.status === 1 || x.status === true || x.status === "1");
        console.log("Filtered items:", result);
        setItems(result);
      }
    } catch (e) {
      console.error("Error fetching items:", e);
      setItems([]);
    }
  };

  const fetchNextGoodsReceiveNumbers = async () => {
    if (!company_id) return;
    try {
      const res = await configServ.getNextGoodsReceiveNumber({
        company_id: company_id,
      });
      if (res.status === 200) {
        const data = res.data;
        setNextAutoNumbers(data);
        setGoodsReceive((prev) => ({
          ...prev,
          purchase_bill_no: prev.purchase_bill_no || data.purchase_bill_no,
          grn_no:
            prev.grn_no ||
            (autoGenerateGrn && !isEdit ? data.grn_no : prev.grn_no) ||
            "",
        }));
      }
    } catch (e) {
      console.error("Failed to fetch next goods receive numbers", e);
    }
  };

  // Generate new GRN number
  const generateNewGrnNumber = async () => {
    try {
      const res = await configServ.getNextGoodsReceiveNumber({
        company_id: company_id,
      });
      if (res.status === 200) {
        const data = res.data;
        setGoodsReceive((prev) => ({
          ...prev,
          grn_no: data.grn_no,
        }));
      }
    } catch (e) {
      console.error("Failed to generate new GRN number", e);
    }
  };

  const handleAutocompleteChange = (event, newValue, itemId) => {
    if (newValue) {
      const updatedItemList = itemList.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            item_id: newValue.id,
            itemFullname: newValue.item_name,
            hsn_code: newValue.hsncode || "",
            unit: newValue.unit || "",
          };
        }
        return item;
      });
      setItemList(updatedItemList);
    }
  };

  const handleQuantityChange = (event, itemId, field) => {
    const value = event.target.value;
    const updatedItemList = itemList.map((item) => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setItemList(updatedItemList);
  };

  const handleAddItem = () => {
    const newId = Math.max(...itemList.map((item) => item.id), 0) + 1;
    const newItem = {
      id: newId,
      itemFullname: "",
      item_id: "",
      hsn_code: "",
      order_qty: "",
      received_qty: "",
      rejected_qty: "",
      unit: "",
    };
    setItemList([...itemList, newItem]);
  };

  const handleDeleteItem = (itemId) => {
    const updatedItemList = itemList.filter((item) => item.id !== itemId);
    setItemList(updatedItemList);
  };

  const disableOrderQty = Boolean(goodsReceive.purchase_order_no);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setGoodsReceive((prevState) => ({
      ...prevState,
      [name]: fieldValue,
    }));

    setGoodsReceiveError((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const validateForm = () => {
    console.log("Validating form...");
    console.log("goodsReceive:", goodsReceive);
    console.log("itemList:", itemList);
    const errors = {};
    const itemErrors = [];

    if (!goodsReceive.location_id && !isEdit) {
      errors.location_id = "Location is required";
    }
    if (!goodsReceive.staff_id) {
      errors.staff_id = "Staff is required";
    }
    if (!autoGenerateGrn && !goodsReceive.grn_no) {
      errors.grn_no = "GRN No is required when manual entry is selected";
    }
    if (!autoGenerateGrn && goodsReceive.grn_no && !grnNoValid) {
      errors.grn_no = "Please enter a valid GRN number";
    }

    itemList.forEach((item, index) => {
      const itemError = {};
      if (!item.item_id) {
        itemError.itemFullname = "Item is required";
      }
      if (!item.order_qty || item.order_qty <= 0) {
        itemError.order_qty = "Order Qty must be > 0";
      }
      if (item.received_qty === "" || item.received_qty === null || item.received_qty === undefined || item.received_qty < 0) {
        itemError.received_qty = "Received Qty is required";
      }
      if (item.rejected_qty === "" || item.rejected_qty === null || item.rejected_qty === undefined) {
        itemError.rejected_qty = "Rejected Qty is required";
      } else if (item.rejected_qty < 0) {
        itemError.rejected_qty = "Rejected Qty cannot be negative";
      } else if (parseFloat(item.rejected_qty) > parseFloat(item.received_qty || 0)) {
        itemError.rejected_qty = "Rejected Qty cannot exceed Received Qty";
      }
      itemErrors.push(itemError);
    });

    setGoodsReceiveError(errors);
    setItemListError(itemErrors);

    const isValid = Object.keys(errors).length === 0 && itemErrors.every((err) => Object.keys(err).length === 0);
    console.log("Validation result:", isValid);
    console.log("Errors:", errors);
    console.log("Item errors:", itemErrors);
    return isValid;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill all required fields correctly",
      });
      return;
    }

    try {
      // Check if purchase order number has already been processed
      // commented out on 1 may 2026 as per discussion with team, to allow multiple GRN against same PO if needed, and to avoid blocking users due to duplicate PO number error when they select existing PO number from dropdown
      // when all items in the PO are not received in one go, which is a common scenario in real life, and we want to allow users to create multiple GRN against same PO number until all items are received, without throwing duplicate PO number error, as long as they select existing PO number from dropdown, which indicates they are aware of existing GRN against that PO number and still want to proceed with creating another GRN for remaining items in that PO.
      // if (goodsReceive.purchase_order_no && !isEdit) {
      //   const checkDuplicateData = {
      //     company_id: company_id,
      //     purchase_order_no: goodsReceive.purchase_order_no,
      //   };
      //   const duplicateCheckRes = await configServ.checkGoodsReceiveByOrderNo(checkDuplicateData);
        
      //   if (duplicateCheckRes.status === 200 && duplicateCheckRes.data && duplicateCheckRes.data.exists) {
      //     Swal.fire({
      //       icon: "warning",
      //       title: "Duplicate Order",
      //       text: "This order number has been already processed",
      //     });
      //     return;
      //   }
      // }

      dispatch(triggerLoader(true));
      const dataToSend = {
        user_id: user_id,
        company_id: company_id,
        is_super: is_super,
        supplier_id: goodsReceive.supplier_id || null,
        location_id: goodsReceive.location_id,
        staff_id: goodsReceive.staff_id,
        purchase_order_no: goodsReceive.purchase_order_no || "",
        purchase_bill_no: goodsReceive.purchase_bill_no || "",
        grn_no: goodsReceive.grn_no || "",
        auto_generate_grn: autoGenerateGrn && !isEdit,
        manual_order_no: false,
        manual_bill_no: !isEdit,
        billing_date: goodsReceive.billing_date || "",
        // address: goodsReceive.address || "",
        reference: goodsReceive.reference || "",
        is_approved: goodsReceive.is_approved,
        remarks: goodsReceive.remarks || "",
        billing_note: goodsReceive.billing_note || "",
        status: goodsReceive.status,
        items: itemList.map((item) => ({
          item_id: parseInt(item.item_id),
          item_name: item.item_name,
          order_qty: parseFloat(item.order_qty) || 0,
          received_qty: parseFloat(item.received_qty) || 0,
          rejected_qty: parseFloat(item.rejected_qty) || 0,
        })),
      };

      console.log("Data being sent to create goods receive:", dataToSend);
      const res = await configServ.createGoodsReceive(dataToSend);
      console.log("API response:", res);
      console.log("API response status:", res.status);

      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Goods receive created successfully",
        });
        setGoodsReceive(goodsReceiveInitial);
        setItemList(itemListInitial);
        setGoodsReceiveError({});
        setItemListError(itemListErrorInitial);
        setAutoGenerateGrn(true);
        setGrnNoValid(false);
        setIsChanged(!isChanged);
        await fetchNextGoodsReceiveNumbers();
        dispatch(triggerLoader(false));
        if (setIsFormOpen) {
          setIsFormOpen(false);
        }
        setEditData({});
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res.message || "Error creating goods receive",
        });
        dispatch(triggerLoader(false));
      }
    } catch (e) {
      console.log("Error creating goods receive:", e);
      console.log("Error response:", e.response);
      console.log("Error message:", e.message);
      console.log("Error data:", e.response?.data);
      
      let errorMessage = "An error occurred while creating goods receive";
      
      // Check if there's a specific error message from the API
      if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.errors) {
        // If it's a validation error with multiple fields
        errorMessage = Object.values(e.response.data.errors).join(", ");
      } else if (e.message) {
        errorMessage = e.message;
      }
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
      dispatch(triggerLoader(false));
    }
  };

  const handleEditClick = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill all required fields correctly",
      });
      return;
    }

    try {
      dispatch(triggerLoader(true));
      const dataToSend = {
        user_id: user_id,
        company_id: company_id,
        is_super: is_super,
        id: goodsReceive.id,
        supplier_id: goodsReceive.supplier_id || null,
        location_id: goodsReceive.location_id,
        staff_id: goodsReceive.staff_id,
        purchase_order_no: goodsReceive.purchase_order_no || "",
        purchase_bill_no: goodsReceive.purchase_bill_no || "",
        billing_date: goodsReceive.billing_date || "",
        address: goodsReceive.address || "",
        reference: goodsReceive.reference || "",
        is_approved: goodsReceive.is_approved,
        remarks: goodsReceive.remarks || "",
        billing_note: goodsReceive.billing_note || "",
        status: goodsReceive.status,
        items: itemList.map((item) => ({
          item_id: parseInt(item.item_id),
          item_name: item.item_name || "",
          order_qty: parseFloat(item.order_qty) || 0,
          received_qty: parseFloat(item.received_qty) || 0,
          rejected_qty: parseFloat(item.rejected_qty) || 0,
        })),
      };

      const res = await configServ.updateGoodsReceive(dataToSend);

      if (res.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Goods receive updated successfully",
        });
        setGoodsReceive(goodsReceiveInitial);
        setItemList(itemListInitial);
        setGoodsReceiveError({});
        setItemListError(itemListErrorInitial);
        setAutoGenerateGrn(true);
        setGrnNoValid(false);
        setIsChanged(!isChanged);
        setIsEdit(false);
        setIsCreate(false);
        setEditData({});
        dispatch(triggerLoader(false));
        if (setIsFormOpen) {
          setIsFormOpen(false);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res.message || "Error updating goods receive",
        });
        dispatch(triggerLoader(false));
      }
    } catch (e) {
      console.log(e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while updating goods receive",
      });
      dispatch(triggerLoader(false));
    }
  };

  const handleResetClick = async () => {
    setGoodsReceive(goodsReceiveInitial);
    setItemList(itemListInitial);
    setGoodsReceiveError({});
    setItemListError(itemListErrorInitial);
    setAutoGenerateGrn(true);
    setGrnNoValid(false);
    setIsEdit(false);
    setIsCreate(false);
    setEditData({});
    await fetchNextGoodsReceiveNumbers();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
        {/* <Box sx={{ mb: 2 }}>
          <CardHeadMenu title="Goods Receive Note" showCreate={false} />
        </Box> */}

        <Box sx={{ backgroundColor: "#E3F3FC", p: 3, borderRadius: 2, mb: 2, boxSizing: "border-box" }}>
          {/* Top Save Button */}
          <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end", gap: 2, spacing: 3, px: 3, pt: 2 }}>
            {isCreate ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UpdateIcon />}
                  onClick={handleEditClick}
                >
                  Update
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={handleResetClick}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color={"secondary"}
                  startIcon={<RotateLeftIcon />}
                  onClick={handleResetClick}
                ></Button>

                <Button
                  variant="contained"
                  color={"success"}
                  startIcon={<SaveIcon />}
                  onClick={handleSaveClick}
                ></Button>
              </>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                sx={{ backgroundColor: "white" }}
                value={
                  goodsReceive.supplier_id
                    ? suppliers.find(
                        (supplier) =>
                          supplier.id === goodsReceive.supplier_id,
                      ) || null
                    : null
                }
                options={suppliers}
                getOptionLabel={(option) => option.fname || ""}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleInputChange({
                      target: { name: "supplier_id", value: newValue.id },
                    });
                    handleInputChange({
                      target: { name: "address", value: newValue.address },
                    });
                    // Load purchase orders for the selected supplier
                    getPurchaseOrderNumbers(newValue.id);
                    // Clear purchase order selection when supplier changes
                    handleOrderNoChange("");
                  } else {
                    // Clear supplier and purchase orders when no supplier selected
                    handleInputChange({
                      target: { name: "supplier_id", value: null },
                    });
                    handleInputChange({
                      target: { name: "address", value: "" },
                    });
                    getPurchaseOrderNumbers();
                    handleOrderNoChange("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier Name"
                    fullWidth
                  />
                )}
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                value={goodsReceive.purchase_order_no || null}
                options={purchaseOrderNumbers}
                getOptionLabel={(option) => 
                  typeof option === 'string' ? option : (option.purchase_order_no || '')
                }
                isOptionEqualToValue={(option, value) => 
                  (typeof option === 'string' ? option : option.purchase_order_no) === 
                  (typeof value === 'string' ? value : value.purchase_order_no)
                }
                onChange={(event, newValue) => {
                  const poNo = typeof newValue === 'string' ? newValue : (newValue?.purchase_order_no || '');
                  handleOrderNoChange(poNo);
                }}
                sx={{ backgroundColor: "white" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Purchase Order No"
                    placeholder="Example: PO202600001"
                  />
                )}
                noOptionsText="No purchase orders available"
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Purchase Bill No"
                placeholder="Example: PB202600001"
                sx={{ backgroundColor: "white" }}
                name="purchase_bill_no"
                value={goodsReceive.purchase_bill_no || ""}
                onChange={handleInputChange}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                disabled={isEdit}
                control={
                  <Checkbox
                    checked={autoGenerateGrn}
                    onClick={() => {
                      const checked = !autoGenerateGrn;
                      setAutoGenerateGrn(checked);
                      if (checked) {
                        // Generate GRN number when checked
                        generateNewGrnNumber();
                      } else {
                        // Clear GRN number when unchecked
                        setGoodsReceive((prev) => ({
                          ...prev,
                          grn_no: "",
                        }));
                        setGrnNoValid(false);
                      }
                    }}
                  />
                }
                label="Auto GRN No"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                disabled={autoGenerateGrn}
                fullWidth
                size="small"
                label="GRN No"
                sx={{ backgroundColor: "white" }}
                name="grn_no"
                value={goodsReceive.grn_no || ""}
                onChange={handleInputChange}
                placeholder={
                  !autoGenerateGrn
                    ? `${nextAutoNumbers.grn_no || "GRN202600001"} (Sample)`
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {autoGenerateGrn || grnNoValid || isEdit ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </InputAdornment>
                  ),
                }}
                error={!!goodsReceiveError.grn_no}
                helperText={
                  autoGenerateGrn
                    ? "Auto-generated GRN number"
                    : "Manual GRN number as per configured prefix style"
                }
                disabled={isEdit}
              />
            </Grid>           

            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Bill Date"
                value={goodsReceive.billing_date ? dayjs(goodsReceive.billing_date) : null}
                onChange={(newValue) => {
                  handleInputChange({
                    target: { name: "billing_date", value: newValue ? newValue.format("YYYY-MM-DD") : "" },
                  });
                }}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", fullWidth: true, sx: { backgroundColor: "white" } } }}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Receipt Date *"
                value={goodsReceive.receipt_date ? dayjs(goodsReceive.receipt_date) : null}
                onChange={(newValue) => {
                  handleInputChange({
                    target: { name: "receipt_date", value: newValue ? newValue.format("YYYY-MM-DD") : "" },
                  });
                }}
                format="DD/MM/YYYY"
                slotProps={{ 
                  textField: { 
                    size: "small", 
                    fullWidth: true, 
                    sx: { backgroundColor: "white" },
                    error: !!goodsReceiveError.receipt_date,
                    helperText: goodsReceiveError.receipt_date
                  } 
                }}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                disablePortal
                id="location-select"
                size="small"
                options={locations}
                getOptionLabel={(option) => `${option.address} (${option.id})` || ""}
                value={
                  locations.find((loc) => loc.id === goodsReceive.location_id) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange({
                    target: { name: "location_id", value: newValue?.id || null },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location *"
                    error={!!goodsReceiveError.location_id}
                    helperText={goodsReceiveError.location_id}
                  />
                )}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                disablePortal
                id="staff-select"
                size="small"
                options={
                    is_admin || is_super ? 
                      staffList
                    : 
                      staffList.filter((s) => s.user_id === user_id)}
                getOptionLabel={(option) => option.fname || ""}
                value={
                  staffList.find((staff) => staff.id === goodsReceive.staff_id) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange({
                    target: { name: "staff_id", value: newValue?.id || null },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Received By *"
                    error={!!goodsReceiveError.staff_id}
                    helperText={goodsReceiveError.staff_id}
                  />
                )}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="supplier ref no / challan"
                fullWidth
                size="small"
                name="reference"
                value={goodsReceive.reference || ""}
                onChange={handleInputChange}
                sx={{ backgroundColor: "white" }}
                disabled={isEdit}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Receipt Note"
                name="remarks"
                value={goodsReceive.remarks || ""}
                onChange={handleInputChange}
                placeholder="Enter additional remarks"
                disabled={isEdit}
              />
            </Grid>

            <Grid container item xs={12} sm={6} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={approvalRequired}
                    disabled={!(is_admin || is_super)}
                    onChange={handleApprovalChange}
                    name="approval_required"
                  />
                }
                label="Approval Required"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={goodsReceive.status}
                    onChange={handleInputChange}
                    name="status"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Items Details:
            </Typography>
            <AddGoodsReceiveItems
              itemList={itemList}
              handleAutocompleteChange={handleAutocompleteChange}
              handleQuantityChange={handleQuantityChange}
              handleAddItem={handleAddItem}
              handleDeleteItem={handleDeleteItem}
              items={items}
              errors={itemListError}
              orderQtyReadOnly={disableOrderQty}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end", spacing: 3, px: 3, pb: 2  }}>
            {isCreate ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UpdateIcon />}
                  onClick={handleEditClick}
                >
                  Update
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={handleResetClick}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<RotateLeftIcon />}
                  onClick={handleResetClick}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveClick}
                >
                  Save
                </Button>
                
              </>
            )}
          </Box>
        </Box>
      </motion.div>

      <AlertDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={title}
        content={dialogContent}
        type={buttonType}
      />
      </>
    </LocalizationProvider>
  );
}

export default memo(GoodsReceiveCard);
